/**
 * Toast — bottom-center, serif italic. Success: 3s auto-dismiss; error: sticky
 * (never auto-dismiss; user must click to dismiss). Per UX §3.21 + AC-155.
 * Per UX §4.3: errors get a "try again" undo-style link where retry is meaningful.
 */
import * as ToastPrim from '@radix-ui/react-toast';
import { X } from '@phosphor-icons/react';
import { cn } from '../../lib/cn';
import { selectToasts, useAppStore, type Toast as ToastModel } from '../../lib/store';

/**
 * ToastViewport — mount once at the app root inside the Provider.
 * Drives toast state from the Zustand store.
 */
export function ToastViewport() {
  const toasts = useAppStore(selectToasts);
  const dismissToast = useAppStore((s) => s.dismissToast);

  return (
    <ToastPrim.Provider swipeDirection="down" duration={3000}>
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={() => dismissToast(t.id)} />
      ))}
      <ToastPrim.Viewport
        className={cn(
          'fixed bottom-6 left-1/2 -translate-x-1/2 z-[60]',
          'flex flex-col items-center gap-2',
          'max-w-md w-[calc(100%-2rem)] outline-none',
        )}
      />
    </ToastPrim.Provider>
  );
}

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: ToastModel;
  onDismiss: () => void;
}) {
  // Per AC-155: success auto-dismisses, error is sticky unless caller passes ms.
  const defaultMs =
    toast.variant === 'error' ? null : (toast.autoDismissMs ?? 3000);
  const duration =
    toast.autoDismissMs === undefined
      ? (defaultMs ?? Infinity)
      : (toast.autoDismissMs ?? Infinity);

  return (
    <ToastPrim.Root
      open
      duration={duration}
      onOpenChange={(open) => {
        if (!open) onDismiss();
      }}
      role={toast.variant === 'error' ? 'alert' : 'status'}
      className={cn(
        'w-full',
        'bg-surface border border-ink-200',
        'px-5 py-4',
        'flex items-center justify-between gap-3',
        'data-[state=open]:animate-[fade-in_280ms_var(--ease-out-slow)]',
        'data-[state=closed]:animate-[fade-out_400ms_var(--ease-out-slow)]',
      )}
    >
      <ToastPrim.Description
        className={cn(
          'font-serif italic text-sm leading-snug',
          toast.variant === 'error' ? 'text-error-muted' : 'text-ink-900',
        )}
      >
        {toast.message}
      </ToastPrim.Description>
      <div className="flex items-center gap-3">
        {toast.undo ? (
          <ToastPrim.Action
            altText={toast.undo.label}
            onClick={() => {
              toast.undo?.onUndo();
              onDismiss();
            }}
            className={cn(
              'font-sans text-xs uppercase tracking-widest text-accent',
              'hover:text-accent-hover',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
            )}
          >
            {toast.undo.label}
          </ToastPrim.Action>
        ) : null}
        <ToastPrim.Close
          aria-label="Dismiss"
          className={cn(
            'text-ink-500 hover:text-ink-900 p-1',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
          )}
        >
          <X size={14} weight="regular" />
        </ToastPrim.Close>
      </div>
    </ToastPrim.Root>
  );
}
